import React, { createContext, useCallback, useState } from 'react';

import Loading from '@justgiving/loading';

import Modal from '../components/Modal/Modal';
import Portal from '../components/Portal/Portal';
import { Toast, ToastProps } from '../components/Toast/Toast';

import styles from '../components/Loading/Loading.scss';

export type AppContext = {
  showToast(toast: ToastProps): void;
  hideToast(): void;
  startLoading(): void;
  stopLoading(): void;
  isSSR: boolean;
  hideModal(): void;
  showModal({ modal, overwrite }: { modal: MODAL; overwrite?: boolean }): void;
  openModal: MODAL | null;
};

export const AppContext = createContext<AppContext | undefined>(undefined);
AppContext.displayName = 'AppContext';

export const AppContextProvider = AppContext.Provider;

export enum MODAL {
  ENCOURAGE_DONATE,
  ENCOURAGE_SHARE,
  SHARE,
  DONATE_TO_YOURSELF,
  CHECKOUT,
}

export const AppProvider: React.FunctionComponent = ({ children }) => {
  const [toast, setToast] = useState<ToastProps | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<MODAL | null>(null);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  const showToast = useCallback((toast: ToastProps) => {
    setToast(toast);
  }, []);

  const startLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  const hideModal = useCallback(() => {
    setOpenModal(null);
  }, []);

  const showModal = useCallback(
    ({ modal, overwrite = false }: { modal: MODAL; overwrite?: boolean }) => {
      setOpenModal(prevModal => {
        if (overwrite) {
          return modal;
        }
        return prevModal !== null ? prevModal : modal;
      });
    },
    [],
  );

  const isSSR = typeof window === 'undefined';

  return (
    <AppContextProvider
      value={{
        showToast,
        hideToast,
        startLoading,
        stopLoading,
        isSSR,
        hideModal,
        showModal,
        openModal,
      }}
    >
      {children}
      {isLoading ? (
        <Modal className={styles.spinner}>
          <Loading />
        </Modal>
      ) : null}
      {toast ? (
        <Portal>
          <Toast onDismiss={hideToast} {...toast} />
        </Portal>
      ) : null}
    </AppContextProvider>
  );
};

AppProvider.displayName = 'AppProvider';
